import React, { useEffect, useState } from "react";
import { api, mediumArt } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import TrackRow from "@/components/TrackRow";
import Section from "@/components/Section";
import { Heart, Music2, Clock3, LogIn } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";

const EmptyLogin = ({ login }) => (
  <div className="flex flex-col items-center justify-center py-32 text-center">
    <Music2 size={48} className="text-white/30 mb-4" />
    <h2 className="font-display text-3xl font-semibold mb-2">Your Library awaits</h2>
    <p className="text-white/50 max-w-md mb-6">Sign in to like songs, build playlists, and pick up where you left off.</p>
    <button
      data-testid="library-login-btn"
      onClick={login}
      className="bg-white text-black font-semibold rounded-full px-7 py-3 flex items-center gap-2 hover:scale-105 transition-transform"
    >
      <LogIn size={16} /> Sign in with Google
    </button>
  </div>
);

const Library = () => {
  const { user, login } = useAuth();
  const { loadLikes } = usePlayer();
  const [likes, setLikes] = useState([]);
  const [recents, setRecents] = useState([]);
  const [playlists, setPlaylists] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/library/likes").then((r) => setLikes(r.data)).catch(() => {});
    api.get("/library/recent").then((r) => setRecents(r.data)).catch(() => {});
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
    loadLikes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) {
    return <div data-testid="library-page"><EmptyLogin login={login} /></div>;
  }

  return (
    <div data-testid="library-page" className="pb-12">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter mb-8">Your Library</h1>

      <Section title="Playlists" testId="library-playlists">
        {playlists.length === 0 ? (
          <p className="text-white/40 text-sm">No playlists yet. Create one from the sidebar.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {playlists.map((p) => {
              const cover = p.tracks?.[0]?.artworkUrl100;
              return (
                <Link
                  key={p.playlist_id}
                  to={`/playlist/${p.playlist_id}`}
                  data-testid={`library-playlist-${p.playlist_id}`}
                  className="group p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.06] border border-transparent hover:border-white/5 transition-all"
                >
                  <div className="aspect-square rounded-md overflow-hidden mb-3 bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center shadow-lg">
                    {cover ? (
                      <img src={mediumArt(cover)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Music2 size={40} className="text-black/60" />
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-xs text-white/50 truncate mt-0.5">{p.tracks?.length || 0} songs</p>
                </Link>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Liked Songs" testId="library-likes">
        {likes.length === 0 ? (
          <p className="text-white/40 text-sm">Songs you like will appear here.</p>
        ) : (
          <div className="flex flex-col">
            {likes.slice(0, 20).map((t, i) => (
              <TrackRow key={t.trackId} track={t} index={i} queue={likes.filter((x) => x.previewUrl)} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Recently Played" testId="library-recent">
        {recents.length === 0 ? (
          <p className="text-white/40 text-sm">Tracks you play will show up here.</p>
        ) : (
          <div className="flex flex-col">
            {recents.slice(0, 15).map((t, i) => (
              <TrackRow key={`${t.trackId}-${i}`} track={t} index={i} queue={recents.filter((x) => x.previewUrl)} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default Library;
